<template>
  <div class="einladung-wrapper">
    <!-- Ladezustand -->
    <div v-if="isLoading" class="state-box">
      <v-progress-circular indeterminate color="primary" size="48" />
      <p class="mt-4">Ihre Einladung wird geladen...</p>
    </div>

    <!-- Ungültiger Link -->
    <div v-else-if="!isValid" class="state-box">
      <v-icon icon="mdi-link-variant-off" size="56" color="#6b7a99" />
      <h2 class="mt-4">Dieser Einladungs-Link ist leider ungültig</h2>
      <p class="mt-2">
        Bitte prüfen Sie den Link aus Ihrer Einladung oder wenden Sie sich an Ihren
        TeamWaizmann-Ansprechpartner.
      </p>
    </div>

    <!-- Erfolgreich angemeldet -->
    <div v-else-if="successData" class="success-card">
      <v-icon icon="mdi-check-circle" size="64" color="#2e7d32" />
      <h2 class="mt-4">Ihre Anmeldung ist bestätigt!</h2>
      <p class="success-webinar mt-3">{{ successData.title }}</p>
      <div class="success-termin mt-2">
        <v-icon start icon="mdi-calendar-check" size="20" />
        {{ successData.beginn }} Uhr
      </div>
      <p class="mt-4">
        Wir haben Ihnen eine Bestätigung per E-Mail geschickt. Die Zugangsdaten zum Webinar
        erhalten Sie ca. 3 Tage vor dem Termin.
      </p>
      <v-btn class="mt-4" variant="tonal" color="primary" @click="resetSuccess">
        weiteres Webinar buchen
      </v-btn>
    </div>

    <!-- Landing Page -->
    <template v-else>
      <section class="einladung-hero">
        <p class="eyebrow mb-2">TeamWaizmann Praxis-Webinare</p>
        <h1 class="hero-title">
          Herzlich willkommen<span v-if="praxisName">, {{ praxisName }}</span>!
        </h1>
        <p class="hero-text">
          Ihre persönliche Einladung zu unseren kostenlosen Experten-Webinaren für
          Zahnarztpraxen. Wählen Sie einfach ein Thema und Ihren Wunschtermin &ndash; die
          Anmeldung dauert keine Minute.
        </p>
        <div class="hero-chips">
          <span class="hero-chip"><v-icon start icon="mdi-gift-outline" size="16" />kostenlos</span>
          <span class="hero-chip"><v-icon start icon="mdi-monitor" size="16" />online</span>
          <span class="hero-chip"><v-icon start icon="mdi-clock-outline" size="16" />ca. 20 Minuten</span>
        </div>
      </section>

      <section v-for="webinar in webinare" :key="webinar.webi_id" class="webinar-card">
        <div class="webinar-head">
          <div class="webinar-head-main">
            <p class="eyebrow mb-1">Webinar</p>
            <h2 class="webinar-title">{{ webinar.title }}</h2>
            <div class="dozent-row mt-3">
              <v-avatar v-if="dozentImage(webinar)" size="52" class="dozent-avatar">
                <v-img :src="dozentImage(webinar)" :alt="webinar.dozent_name" cover />
              </v-avatar>
              <v-avatar v-else size="52" color="#eef6ff" class="dozent-avatar">
                <v-icon icon="mdi-account-tie" color="#3166a5" />
              </v-avatar>
              <div>
                <div class="dozent-name">{{ webinar.dozent_name }}</div>
                <div class="dozent-sub">
                  Referent(in) &middot; Dauer ca. {{ webinar.webi_laenge || 20 }} Minuten
                </div>
              </div>
            </div>
            <p v-if="webinar.webi_text" class="webinar-text mt-3">{{ webinar.webi_text }}</p>
          </div>
        </div>

        <div class="termine-label">Wählen Sie Ihren Termin:</div>
        <div class="termine-row">
          <button
            v-for="termin in webinar.termine"
            :key="termin.wter_id"
            type="button"
            class="termin-btn"
            :class="{
              selected: selectedTerminId === termin.wter_id,
              disabled: termin.status !== 1,
            }"
            :disabled="termin.status !== 1"
            @click="selectTermin(webinar, termin)"
          >
            <span class="termin-datum">{{ terminDatum(termin) }}</span>
            <span class="termin-zeit">{{ terminZeit(termin) }} Uhr</span>
            <span v-if="termin.status === 1" class="termin-frei">
              noch {{ termin.plaetze_frei }} Plätze frei
            </span>
            <span v-else class="termin-voll">{{ termin.statusText }}</span>
          </button>
        </div>

        <!-- Inline-Anmeldeformular -->
        <v-expand-transition>
          <div v-if="selectedWebinarId === webinar.webi_id && selectedTerminId" class="anmelde-box">
            <h3 class="anmelde-title">
              <v-icon start icon="mdi-calendar-edit" color="#3166a5" />
              Anmeldung für {{ selectedTerminBeginn }} Uhr
            </h3>
            <v-form @submit.prevent="submitAnmeldung">
              <v-text-field
                v-model="form.wteil_pers_name"
                label="Wer nimmt am Webinar teil? (Name)"
                :error-messages="formError.wteil_pers_name"
                variant="outlined"
                density="comfortable"
                @input="formError.wteil_pers_name = ''"
              />
              <v-text-field
                v-model="form.wteil_pers_email"
                label="An welche E-Mail senden wir den Teilnahme-Link?"
                :error-messages="formError.wteil_pers_email"
                variant="outlined"
                density="comfortable"
                @blur="validateEmailField"
                @input="formError.wteil_pers_email = ''"
              />
              <v-text-field
                v-model="form.wteil_pers_praxis"
                label="Name Ihrer Praxis"
                variant="outlined"
                density="comfortable"
              />
              <v-radio-group v-model="form.wteil_pers_type" class="ma-0">
                <v-radio label="Sie sind Zahnarzt/Zahnärztin" color="green" :value="1" />
                <v-radio label="Praxis-Mitarbeiter/in" color="green" :value="2" />
              </v-radio-group>

              <v-alert
                v-if="serverError"
                type="error"
                variant="tonal"
                density="comfortable"
                class="mb-4"
              >
                {{ serverError }}
              </v-alert>

              <v-btn
                type="submit"
                color="primary"
                size="large"
                :loading="isSubmitting"
                class="submit-btn"
              >
                jetzt kostenlos anmelden
              </v-btn>
            </v-form>
          </div>
        </v-expand-transition>
      </section>

      <div v-if="webinare.length === 0" class="state-box">
        <v-icon icon="mdi-calendar-remove" size="56" color="#6b7a99" />
        <h2 class="mt-4">Aktuell sind keine Webinar-Termine verfügbar</h2>
        <p class="mt-2">Schauen Sie bald wieder vorbei &ndash; neue Termine folgen in Kürze.</p>
      </div>
    </template>
  </div>
</template>

<script setup>
import { inject, onMounted, ref, computed } from "vue";
import { useRoute } from "vue-router";
import regexHelper from "@/plugins/regexHelper";

const baseCalls = inject("baseCalls");
const route = useRoute();

const isLoading = ref(true);
const isValid = ref(false);
const visit = ref(null);
const webinare = ref([]);

const selectedWebinarId = ref(0);
const selectedTerminId = ref(0);
const selectedTerminBeginn = ref("");
const isSubmitting = ref(false);
const serverError = ref("");
const successData = ref(null);

const form = ref({
  wteil_pers_name: "",
  wteil_pers_email: "",
  wteil_pers_praxis: "",
  wteil_pers_type: 1,
});
const formError = ref({
  wteil_pers_name: "",
  wteil_pers_email: "",
});

const praxisName = computed(() => {
  const firma = visit.value?.visit_firma;
  return firma && String(firma).trim() ? String(firma).trim() : "";
});

const wochentage = ["So", "Mo", "Di", "Mi", "Do", "Fr", "Sa"];

function terminDatum(termin) {
  const date = new Date(String(termin.wter_beginn).replace(" ", "T"));
  if (isNaN(date.getTime())) {
    return termin.beginn;
  }
  const tag = String(date.getDate()).padStart(2, "0");
  const monat = String(date.getMonth() + 1).padStart(2, "0");
  return `${wochentage[date.getDay()]}, ${tag}.${monat}.${date.getFullYear()}`;
}

function terminZeit(termin) {
  const date = new Date(String(termin.wter_beginn).replace(" ", "T"));
  if (isNaN(date.getTime())) {
    return "";
  }
  const stunden = String(date.getHours()).padStart(2, "0");
  const minuten = String(date.getMinutes()).padStart(2, "0");
  return `${stunden}:${minuten}`;
}

function dozentImage(webinar) {
  const raw = webinar.dozent_image;
  if (!raw || !String(raw).trim()) {
    return "";
  }
  const value = String(raw).trim();
  if (value.startsWith("http://") || value.startsWith("https://") || value.startsWith("/")) {
    return value;
  }
  const normalized = value
    .replace(/^\.?\//, "")
    .replace(/^assets\/site\//, "")
    .replace(/^site\//, "");
  try {
    return require("@/assets/site/" + normalized);
  } catch (err) {
    return "";
  }
}

function selectTermin(webinar, termin) {
  serverError.value = "";
  selectedWebinarId.value = webinar.webi_id;
  selectedTerminId.value = termin.wter_id;
  selectedTerminBeginn.value = termin.beginn;
}

function validateEmailField() {
  const [showError, textError] = regexHelper.validateEmail(form.value.wteil_pers_email);
  formError.value.wteil_pers_email = showError ? textError : "";
  return !showError;
}

function validateForm() {
  let ok = true;
  if (!validateEmailField()) {
    ok = false;
  }
  if (!form.value.wteil_pers_name || !form.value.wteil_pers_name.trim()) {
    formError.value.wteil_pers_name = "Bitte geben Sie Ihren Namen ein";
    ok = false;
  }
  return ok;
}

async function submitAnmeldung() {
  serverError.value = "";
  if (!validateForm()) {
    return;
  }
  isSubmitting.value = true;
  try {
    const response = await baseCalls.webinarePost({
      action: "inserteinladungteilnahme",
      visit_id: Number(route.params.visit_id),
      code: Number(route.params.code) || 0,
      wteil_termin_id: selectedTerminId.value,
      wteil_pers_name: form.value.wteil_pers_name.trim(),
      wteil_pers_email: form.value.wteil_pers_email.trim(),
      wteil_pers_praxis: form.value.wteil_pers_praxis.trim(),
      wteil_pers_type: form.value.wteil_pers_type,
    });
    if (response.data && response.data.success) {
      successData.value = {
        title: response.data.title,
        beginn: response.data.beginn,
      };
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      serverError.value =
        response.data?.message || "Ihre Anmeldung konnte nicht gespeichert werden.";
      if (serverError.value.includes("ausgebucht")) {
        await getEinladung();
      }
    }
  } catch (err) {
    serverError.value = "Es ist ein technischer Fehler aufgetreten. Bitte versuchen Sie es erneut.";
  } finally {
    isSubmitting.value = false;
  }
}

function resetSuccess() {
  successData.value = null;
  selectedWebinarId.value = 0;
  selectedTerminId.value = 0;
  serverError.value = "";
  getEinladung();
}

async function getEinladung() {
  try {
    const response = await baseCalls.webinareGet({
      action: "einladung",
      id: route.params.visit_id,
      code: Number(route.params.code) || 0,
    });
    if (response.data && response.data.valid) {
      isValid.value = true;
      visit.value = response.data.visit;
      webinare.value = response.data.webinare || [];
      if (!form.value.wteil_pers_praxis && praxisName.value) {
        form.value.wteil_pers_praxis = praxisName.value;
      }
    } else {
      isValid.value = false;
    }
  } catch (err) {
    isValid.value = false;
  } finally {
    isLoading.value = false;
  }
}

onMounted(() => {
  getEinladung();
});
</script>

<style scoped>
.einladung-wrapper {
  display: flex;
  flex-direction: column;
  gap: 20px;
  max-width: 860px;
  margin: 0 auto;
}

.state-box {
  padding: 56px 24px;
  text-align: center;
  border-radius: 12px;
  border: 1px solid #e3e8f1;
  background: #ffffff;
  color: #4a5568;
}

.state-box h2 {
  color: #1f3a63;
}

.einladung-hero {
  padding: 32px;
  border-radius: 12px;
  border: 1px solid #e3e8f1;
  background: linear-gradient(180deg, #f8fbff 0%, #ffffff 100%);
}

.eyebrow {
  text-transform: uppercase;
  letter-spacing: 0.08em;
  font-size: 12px;
  font-weight: 700;
  color: #6b7a99;
}

.hero-title {
  font-size: 30px;
  line-height: 1.25;
  margin-bottom: 12px;
  color: #1f3a63;
}

.hero-text {
  color: #4a5568;
  line-height: 1.55;
  max-width: 640px;
}

.hero-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 16px;
}

.hero-chip {
  display: inline-flex;
  align-items: center;
  padding: 6px 14px;
  border-radius: 999px;
  border: 1px solid #d5e6ff;
  background: #eef6ff;
  color: #3166a5;
  font-size: 13px;
  font-weight: 600;
}

.webinar-card {
  padding: 24px 28px 28px;
  border-radius: 12px;
  border: 1px solid #e3e8f1;
  background: #ffffff;
  box-shadow: 0 6px 18px rgba(20, 45, 90, 0.05);
}

.webinar-title {
  font-size: 21px;
  line-height: 1.35;
  color: #1f3a63;
}

.dozent-row {
  display: flex;
  align-items: center;
  gap: 12px;
}

.dozent-avatar {
  border: 2px solid #e3e8f1;
}

.dozent-name {
  font-weight: 700;
  color: #1f3a63;
}

.dozent-sub {
  font-size: 13px;
  color: #6b7a99;
}

.webinar-text {
  color: #4a5568;
  line-height: 1.55;
}

.termine-label {
  margin-top: 18px;
  margin-bottom: 10px;
  font-weight: 700;
  color: #1f3a63;
}

.termine-row {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 10px;
}

.termin-btn {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
  padding: 12px 16px;
  border-radius: 10px;
  border: 2px solid #e1e6ef;
  background: #f7f9fc;
  cursor: pointer;
  text-align: left;
  transition: border-color 0.15s ease, background 0.15s ease;
}

.termin-btn:hover:not(.disabled) {
  border-color: #3166a5;
}

.termin-btn.selected {
  border-color: #3166a5;
  background: #eef6ff;
}

.termin-btn.disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.termin-datum {
  font-weight: 700;
  color: #1f3a63;
}

.termin-zeit {
  color: #4a5568;
  font-size: 14px;
}

.termin-frei {
  font-size: 12px;
  font-weight: 600;
  color: #2e7d32;
}

.termin-voll {
  font-size: 12px;
  font-weight: 600;
  color: #c62828;
}

.anmelde-box {
  margin-top: 18px;
  padding: 20px;
  border-radius: 10px;
  border: 1px solid #d5e6ff;
  background: #f8fbff;
}

.anmelde-title {
  margin-bottom: 16px;
  color: #1f3a63;
  font-size: 17px;
}

.submit-btn {
  width: 100%;
}

.success-card {
  padding: 48px 32px;
  text-align: center;
  border-radius: 12px;
  border: 1px solid #cde8cf;
  background: linear-gradient(180deg, #f4fbf4 0%, #ffffff 100%);
  color: #4a5568;
}

.success-card h2 {
  color: #1f3a63;
}

.success-webinar {
  font-weight: 700;
  font-size: 17px;
  color: #1f3a63;
}

.success-termin {
  display: inline-flex;
  align-items: center;
  padding: 8px 18px;
  border-radius: 999px;
  background: #e8f5e9;
  color: #2e7d32;
  font-weight: 700;
}

@media (max-width: 768px) {
  .einladung-hero,
  .webinar-card {
    padding: 20px;
  }

  .hero-title {
    font-size: 24px;
  }
}
</style>
